import { Component, OnInit } from '@angular/core';
import { CondoUser } from '@api/model/condo-user';
import { UserService } from '@api/service/user.service';
import { EcondosQuery } from '@api/model/query';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { NzModalService } from 'ng-zorro-antd/modal';
import { ModalFilterComponent } from '../../../components/modal-filter/modal-filter.component';
import { EcondosFilter } from '@api/util/filter';
import { removeAccents, replaceVowelsToAccentedVowels } from '@api/util/util';

@Component({
  selector: 'app-users-page',
  templateUrl: './users-page.component.html',
  styleUrls: ['./users-page.component.scss']
})
export class UsersPageComponent implements OnInit {
  users: CondoUser[] = [];

  page = 1;
  numberOfActiveFilters = 0;

  query: EcondosQuery = { $select: 'firstName lastName picture' };
  customQuery: Record<string, any> = {};
  filters: EcondosFilter[] = [];

  constructor(
    private userService: UserService,
    private notification: NzNotificationService,
    private modal: NzModalService
  ) {
    this.filters = [
      {
        element: 'input',
        elementType: 'text',
        elementSize: 'medium',
        name: '_id',
        label: 'ID do usuário',
        placeholder: 'Informe o id',
        searchType: 'match'
      },
      {
        element: 'input',
        elementType: 'text',
        elementSize: 'medium',
        name: 'firstName',
        label: 'Nome',
        placeholder: 'Informe o nome',
        searchType: 'match'
      },
      {
        element: 'input',
        elementType: 'text',
        elementSize: 'medium',
        name: 'email',
        label: 'E-mail',
        placeholder: 'Informe o e-mail',
        searchType: 'match'
      },
      {
        element: 'select',
        elementSize: 'medium',
        label: 'Administrador do sistema',
        name: 'isSystemAdmin',
        selectOptions: [
          { value: '', label: 'Todos' },
          { value: 'true', label: 'Sim' },
          { value: 'false', label: 'Não' }
        ],
        searchType: 'match'
      }
    ];
  }

  ngOnInit(): void {
    this.getData(this.query);
  }

  getData(query: EcondosQuery): void {
    this.userService.getAllUsers(query).subscribe({
      next: users => {
        this.users = users;
        this.page = 1;
      },
      error: () => {
        this.notification.create('error', 'Falha ao buscar dados', 'Verifique os dados e tente novamente');
      }
    });
  }

  changePage(page: number): void {
    this.page = page;
  }

  openFilterModal(): void {
    const modal = this.modal.create({
      nzTitle: 'Filtro',
      nzClosable: true,
      nzCloseIcon: 'close',
      nzContent: ModalFilterComponent,
      nzData: { initialFilters: this.filters, initialQuery: this.customQuery },
      nzWidth: 400,
      nzFooter: null
    });

    modal.afterClose.subscribe(result => {
      if (result) {
        const query = { ...this.query, ...result.query };
        const filters = result.filters;
        const nameFilter = filters.find((f: { name: string; value: any }) => f.name === 'firstName' && f.value);

        if (nameFilter) {
          let searchTerm = nameFilter.value.trim().toLowerCase();
          let terms = searchTerm.split(' ');
          terms = terms.map((word: string | undefined) => removeAccents(word));
          terms = terms.map(replaceVowelsToAccentedVowels);

          terms.forEach((term: any, i: any) => {
            query[`$and[${i}][$or][0][firstName][$regex]`] = term;
            query[`$and[${i}][$or][0][firstName][$options]`] = 'i';
            query[`$and[${i}][$or][1][lastName][$regex]`] = term;
            query[`$and[${i}][$or][1][lastName][$options]`] = 'i';
          });

          delete query['firstName']; // evita conflito
        }

        this.customQuery = query;
        this.filters = filters;

        this.countActiveFilters(filters);
        this.getData(this.customQuery);
      }
    });
  }

  countActiveFilters(filters: EcondosFilter[]) {
    this.numberOfActiveFilters = filters.reduce((accumulator, currentValue) => {
      if (currentValue.value) {
        return accumulator + 1;
      }

      return accumulator;
    }, 0);
  }

  removeIndividualFilter({ index }: { index: number }): void {
    const filter = this.filters[index];

    filter.value = '';
    filter.valueLabel = '';

    if (filter.name) {
      if (filter.name === 'firstName') {
        // Remove todas as entradas que envolvem firstName/lastName com $regex
        Object.keys(this.customQuery).forEach(key => {
          if (key.includes('firstName') || key.includes('lastName')) {
            delete this.customQuery[key];
          }
        });
      } else {
        delete this.customQuery[filter.name];

        if (['isSystemAdmin', 'role'].includes(filter.name)) {
          delete this.customQuery[filter.name];
        }
      }
    }

    this.numberOfActiveFilters--;

    this.customQuery = { ...this.query, ...this.customQuery };

    this.getData(this.customQuery);
  }

  clearFilters(): void {
    this.filters = this.filters.map(f => ({
      ...f,
      value: '',
      valueLabel: ''
    }));

    this.customQuery = {};
    this.numberOfActiveFilters = 0;

    this.query = { $select: 'firstName lastName picture email' };

    this.getData(this.query);
  }
}
